import * as React from "react";
import { useTransition, useSpring, config, animated } from "react-spring";
import classNames from "classnames";
import ReactTooltip from "react-tooltip";
import { useDrag } from "react-use-gesture";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import useStyles from "./FloatingDebugPanel.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { Panel } from "./panels/Panel";

const AnimatedFontAwesomeIcon = animated(FontAwesomeIcon);

interface ContainerOptions {
    open?: boolean;
    xy?: [number, number];
}

const makeContainerProps = ({ open = false, xy: [x, y] = [0, 0] }: ContainerOptions = {}) => ({
    config: config.stiff,
    immediate: (name) => name === "top" || name === "left",
    width: open ? 450 : 59,
    height: open ? 500 : 59,
    margin: 60,
    top: y,
    left: x,
});

interface Props {
    title: string;
    panels: Panel[];
    selected: string;
    onSelectedChanged(selected: string): void;
}

export default function FloatingDebugPanel({ title, panels, selected, onSelectedChanged }: Props) {
    const classes = useStyles({ selected });
    const { width, height } = useWindowDimensions();
    const [open, setOpen] = React.useState(false);
    const [containerProps, setContainerProps] = useSpring(() => makeContainerProps());
    const buttonRef = React.useRef<HTMLDivElement>();
    const gestureStateRef = React.useRef<any>();

    React.useEffect(() => {
        if (gestureStateRef.current)
            setContainerProps(makeContainerProps({ xy: gestureStateRef.current.offset, open }));
    }, [open, gestureStateRef, setContainerProps]);

    const bind = useDrag(
        (gs) => {
            const { tap, offset, event } = gs;
            gestureStateRef.current = gs;
            const target = event.target as Element;
            if (tap && buttonRef.current.contains(target)) setOpen((o) => !o);
            else setContainerProps(makeContainerProps({ xy: offset, open }));
        },
        {
            bounds: { top: -60, left: -60, right: width - 180, bottom: height - 280 },
            filterTaps: true,
        },
    );

    const selectedPanel = panels.find((p) => p.id === selected);

    const panelTransitions = useTransition(open ? [selectedPanel] : [], null, {
        config: config.stiff,
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
    });

    const panelBtnTransitions = useTransition(open ? panels : [], (p) => p.id, {
        config: config.stiff,
        unique: true,
        trail: 200 / panels.length,
        from: { opacity: 0, transform: "scale(0)" },
        enter: { opacity: 1, transform: "scale(1)" },
        leave: { opacity: 0, transform: "scale(0)" },
    });

    const toggleBtnTransitions = useTransition(open, null, {
        from: { opacity: 0, transform: "scale(0)" },
        initial: { opacity: 1, transform: "scale(1)" },
        enter: { opacity: 1, transform: "scale(1)" },
        leave: { opacity: 0, transform: "scale(0)" },
    });

    return (
        <animated.div className={classes.floatingContainer} style={containerProps}>
            <div className={classes.toolbar} {...bind()}>
                <div className={classes.widgetToggleButton} ref={buttonRef}>
                    {toggleBtnTransitions.map(({ item, props, key }) =>
                        item ? (
                            <AnimatedFontAwesomeIcon
                                key={key}
                                icon={["far", "chevron-up"]}
                                size="xs"
                                className={classes.widgetToggleButtonIcon}
                                style={props}
                            />
                        ) : (
                            <AnimatedFontAwesomeIcon
                                key={key}
                                icon={["fas", "vial"]}
                                className={classes.widgetToggleButtonIcon}
                                style={props}
                            />
                        ),
                    )}
                </div>
                <div className={classes.titleContainer}>
                    <h3 className={classes.title}>{title}</h3>
                    <div
                        className={classes.settingsButton}
                        onClick={() => onSelectedChanged("settings")}
                    >
                        <FontAwesomeIcon
                            size="lg"
                            icon={["fas", "cog"]}
                            transform="shrink-5"
                            mask={["fas", "circle"]}
                        />
                    </div>
                </div>
            </div>
            <div className={classes.panelButtonContainer}>
                {panelBtnTransitions
                    .filter(({ item: { exclude } }) => !exclude)
                    .map(({ item: { onClick, id, icon, active, title }, props }) => (
                        <animated.div
                            key={id}
                            data-tip={title}
                            style={props}
                            onClick={onClick || (() => onSelectedChanged(id))}
                            className={classNames(
                                classes.panelButton,
                                active && classes.panelButtonActive,
                            )}
                        >
                            <div className={classes.panelButtonIcon}>{icon}</div>
                        </animated.div>
                    ))}
            </div>
            {panelTransitions.map(({ item: { content, index, exclude }, key, props }) => (
                <animated.div key={key} className={classes.panel} style={props}>
                    {!exclude && (
                        <>
                            <div
                                style={{ top: 40 + index * 56 }}
                                className={classes.panelIndicatorShadow}
                            />
                            <div
                                style={{ top: 42 + index * 56 }}
                                className={classes.panelIndicator}
                            />
                        </>
                    )}
                    <div className={classes.panelContainer}>
                        <animated.div className={classes.panelContent}>{content}</animated.div>
                    </div>
                </animated.div>
            ))}
            <ReactTooltip effect="solid" />
        </animated.div>
    );
}
